import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Plus, Search, Star, Trash2 } from "lucide-react";
import {
    RichTextEditorComponent,
    Toolbar,
    Image,
    Link,
    HtmlEditor,
    QuickToolbar,
    Inject,
    type ToolbarSettingsModel
} from "@syncfusion/ej2-react-richtexteditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
    useCreateIdentity,
    useDeleteIdentity,
    useIdentities,
    useSetDefaultIdentity,
    useUpdateIdentity
} from "@/api/hooks";
import type { MailIdentity } from "@/api/mailApi";
import { AiTextGenerator } from "./AiTextGenerator";

import "@syncfusion/ej2-base/styles/fluent2.css";
import "@syncfusion/ej2-buttons/styles/fluent2.css";
import "@syncfusion/ej2-inputs/styles/fluent2.css";
import "@syncfusion/ej2-dropdowns/styles/fluent2.css";
import "@syncfusion/ej2-lists/styles/fluent2.css";
import "@syncfusion/ej2-splitbuttons/styles/fluent2.css";
import "@syncfusion/ej2-popups/styles/fluent2.css";
import "@syncfusion/ej2-navigations/styles/fluent2.css";
import "@syncfusion/ej2-richtexteditor/styles/fluent2.css";

const signatureToolbar: ToolbarSettingsModel = {
    items: [
        "Bold", "Italic", "Underline", "|",
        "Formats", "Alignments", "|",
        "OrderedList", "UnorderedList", "|",
        "CreateLink", "Image", "|",
        "Undo", "Redo"
    ]
};

interface DraftState {
    displayName: string;
    emailAddress: string;
    replyTo: string;
    organisation: string;
    signatureHtml: string;
    sortOrder: number;
}

function toDraft(identity: MailIdentity): DraftState {
    return {
        displayName: identity.displayName,
        emailAddress: identity.emailAddress,
        replyTo: identity.replyTo ?? "",
        organisation: identity.organisation ?? "",
        signatureHtml: identity.signatureHtml ?? "",
        sortOrder: identity.sortOrder
    };
}

function newDraft(): DraftState {
    return {
        displayName: "",
        emailAddress: "",
        replyTo: "",
        organisation: "",
        signatureHtml: "",
        sortOrder: 0
    };
}

export function IdentitiesPage() {
    const identities = useIdentities();
    const create = useCreateIdentity();
    const update = useUpdateIdentity();
    const remove = useDeleteIdentity();
    const setDefault = useSetDefaultIdentity();

    const list = identities.data ?? [];

    const [selectedId, setSelectedId] = useState<string | "new" | null>(null);
    const [draft, setDraft] = useState<DraftState>(newDraft);
    const [filter, setFilter] = useState("");

    const visibleList = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (q.length === 0) return list;
        return list.filter((i) =>
            (i.displayName ?? "").toLowerCase().includes(q) ||
            i.emailAddress.toLowerCase().includes(q)
        );
    }, [list, filter]);
    const editorRef = useRef<RichTextEditorComponent>(null);

    // Select the first identity once the list arrives.
    useEffect(() => {
        if (selectedId !== null || list.length === 0) return;
        setSelectedId(list[0]!.id);
        setDraft(toDraft(list[0]!));
    }, [list, selectedId]);

    const selectedIdentity = useMemo(
        () => (selectedId && selectedId !== "new" ? list.find((i) => i.id === selectedId) ?? null : null),
        [list, selectedId]
    );

    const handleSelect = (identity: MailIdentity) => {
        setSelectedId(identity.id);
        setDraft(toDraft(identity));
        const rte = editorRef.current;
        if (rte) {
            rte.value = identity.signatureHtml ?? "";
            rte.dataBind();
        }
    };

    const handleNew = () => {
        setSelectedId("new");
        setDraft(newDraft());
        const rte = editorRef.current;
        if (rte) {
            rte.value = "";
            rte.dataBind();
        }
    };

    const handleSave = () => {
        const body = editorRef.current?.getHtml() ?? "";
        const payload = {
            displayName: draft.displayName.trim(),
            emailAddress: draft.emailAddress.trim(),
            replyTo: draft.replyTo.trim() || null,
            organisation: draft.organisation.trim() || null,
            signatureHtml: body,
            isDefault: selectedIdentity?.isDefault ?? list.length === 0,
            sortOrder: draft.sortOrder
        };

        if (selectedId === "new" || selectedIdentity == null) {
            create.mutate(payload, {
                onSuccess: (row) => {
                    setSelectedId(row.id);
                }
            });
        } else {
            update.mutate({ id: selectedIdentity.id, payload });
        }
    };

    const handleDelete = () => {
        if (!selectedIdentity) return;
        const ok = window.confirm(`Delete identity "${selectedIdentity.emailAddress}"?`);
        if (!ok) return;
        remove.mutate(selectedIdentity.id, {
            onSuccess: () => {
                setSelectedId(null);
                setDraft(newDraft());
            }
        });
    };

    const handleSetDefault = () => {
        if (!selectedIdentity) return;
        setDefault.mutate(selectedIdentity.id);
    };

    const busy = create.isPending || update.isPending || remove.isPending || setDefault.isPending;

    return (
        <div className="mx-auto max-w-5xl">
            <header className="mb-4">
                <h2 className="text-xl font-semibold">Identities & signatures</h2>
                <p className="text-sm text-muted-foreground">
                    Choose which address you send from, and what signs every message.
                </p>
            </header>

            {identities.isPending ? (
                <p className="text-sm text-muted-foreground">Loading identities…</p>
            ) : identities.isError ? (
                <p className="text-sm text-destructive">{identities.error?.message}</p>
            ) : (
                <div className="grid grid-cols-[260px_1fr] gap-6">
                    <aside className="flex flex-col gap-0.5 rounded-lg border bg-card p-2">
                        {list.length > 0 ? (
                            <div className="relative mb-1">
                                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" aria-hidden />
                                <input
                                    type="search"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Filter identities"
                                    aria-label="Filter identities"
                                    className="h-7 w-full rounded-md border bg-background pl-7 pr-2 text-xs outline-none focus:border-primary"
                                />
                            </div>
                        ) : null}
                        {visibleList.map((i) => (
                            <button
                                type="button"
                                key={i.id}
                                onClick={() => handleSelect(i)}
                                className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent ${
                                    selectedId === i.id ? "bg-accent" : ""
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium">
                                        {i.displayName || i.emailAddress}
                                    </div>
                                    <div className="truncate text-xs text-muted-foreground">{i.emailAddress}</div>
                                </div>
                                {i.isDefault ? <Star className="h-3.5 w-3.5 shrink-0 text-amber-500" /> : null}
                            </button>
                        ))}
                        {list.length > 0 && visibleList.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-muted-foreground">
                                No identities match &ldquo;{filter}&rdquo;.
                            </p>
                        ) : null}
                        <Separator className="my-2" />
                        <Button variant="outline" size="sm" onClick={handleNew}>
                            <Plus className="h-4 w-4" /> New identity
                        </Button>
                    </aside>

                    <section className="rounded-lg border bg-card p-5">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Display name">
                                <Input
                                    value={draft.displayName}
                                    onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                                    placeholder="Hemant Kumar"
                                />
                            </Field>
                            <Field label="Email">
                                <Input
                                    type="email"
                                    value={draft.emailAddress}
                                    onChange={(e) => setDraft((d) => ({ ...d, emailAddress: e.target.value }))}
                                    placeholder="hemant@athavita.com"
                                />
                            </Field>
                            <Field label="Reply-To (optional)">
                                <Input
                                    type="email"
                                    value={draft.replyTo}
                                    onChange={(e) => setDraft((d) => ({ ...d, replyTo: e.target.value }))}
                                />
                            </Field>
                            <Field label="Organisation (optional)">
                                <Input
                                    value={draft.organisation}
                                    onChange={(e) => setDraft((d) => ({ ...d, organisation: e.target.value }))}
                                />
                            </Field>
                        </div>

                        <div className="mt-5">
                            <div className="mb-1 flex items-center justify-between gap-2">
                                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    Signature
                                </label>
                                <AiTextGenerator
                                    mode="signature"
                                    title="Generate signature"
                                    firstPlaceholder='Optional: title, phone, website, tone — e.g. "Product Manager, +91…, friendly"'
                                    buildInitial={(input) => [
                                        draft.displayName && `Name: ${draft.displayName}`,
                                        draft.emailAddress && `Email: ${draft.emailAddress}`,
                                        draft.organisation && `Company: ${draft.organisation}`,
                                        input.trim() && `Details / tone: ${input.trim()}`
                                    ].filter(Boolean).join("\n") || "Create a professional signature."}
                                    onApply={(html) => {
                                        const rte = editorRef.current;
                                        if (rte) { rte.value = html; rte.dataBind(); }
                                        setDraft((d) => ({ ...d, signatureHtml: html }));
                                    }}
                                />
                            </div>
                            <RichTextEditorComponent
                                ref={editorRef}
                                toolbarSettings={signatureToolbar}
                                placeholder="Signature (HTML allowed)"
                                height={200}
                                value={draft.signatureHtml}
                            >
                                <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar]} />
                            </RichTextEditorComponent>
                        </div>

                        <div className="mt-5 flex items-center justify-between">
                            <div className="flex gap-2">
                                {selectedIdentity && !selectedIdentity.isDefault ? (
                                    <Button variant="outline" size="sm" onClick={handleSetDefault} disabled={busy}>
                                        <Star className="h-4 w-4" /> Make default
                                    </Button>
                                ) : null}
                                {selectedIdentity ? (
                                    <Button variant="outline" size="sm" onClick={handleDelete} disabled={busy}>
                                        <Trash2 className="h-4 w-4" /> Delete
                                    </Button>
                                ) : null}
                            </div>
                            <Button size="sm" onClick={handleSave} disabled={busy}>
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                {selectedId === "new" || !selectedIdentity ? "Create" : "Save"}
                            </Button>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </label>
            {children}
        </div>
    );
}

